import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import FactoryIcon from "@mui/icons-material/Factory";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import DataGridViewport from "../components/DataGridViewport";
import LocalSelector from "./components/LocalSelector";
import { useUserContext } from "../hooks/UserContext";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import {
  fetchConfirmarProduccion,
  fetchGetProductosProduccion,
  fetchGetRecetaProduccion,
  ProduccionInsumo,
  ProduccionProducto,
} from "../services/produccionService";
import { isAbortError } from "../services/fetchService";
import { useNotification } from "../hooks/NotificationContext";

const toMoney = (value: number) => CurrencyFormatter.format(Number(value || 0));

const Produccion = () => {
  const { user } = useUserContext();
  const { showNotification } = useNotification();
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [searchToApply, setSearchToApply] = useState("");
  const [soloConReceta, setSoloConReceta] = useState(true);
  const [productos, setProductos] = useState<ProduccionProducto[]>([]);
  const [totalProductos, setTotalProductos] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState<ProduccionProducto | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [cantidad, setCantidad] = useState(1);
  const [insumos, setInsumos] = useState<ProduccionInsumo[]>([]);
  const [loadingReceta, setLoadingReceta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actualizarCosto, setActualizarCosto] = useState(true);
  const recetaAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPaginationModel((current) => ({ ...current, page: 0 }));
      setSearchToApply(search);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!user || !idLocal) {
      setProductos([]);
      setTotalProductos(0);
      return;
    }

    const controller = new AbortController();
    setLoadingProductos(true);
    fetchGetProductosProduccion(idLocal, user, paginationModel.page, paginationModel.pageSize, searchToApply, soloConReceta, controller.signal)
      .then((data) => {
        setProductos(data.items);
        setTotalProductos(data.total);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        setProductos([]);
        setTotalProductos(0);
        showNotification("No se pudieron cargar los productos para produccion.", "error");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingProductos(false);
      });

    return () => controller.abort();
  }, [idLocal, paginationModel.page, paginationModel.pageSize, searchToApply, showNotification, soloConReceta, user]);

  useEffect(() => {
    return () => recetaAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    setInsumos((actuales) => actuales.map((item) => ({
      ...item,
      cantidadTotal: Number((item.cantidadUnitario * cantidad).toFixed(4)),
    })));
  }, [cantidad]);

  const seleccionarProducto = (producto: ProduccionProducto) => {
    setProductoSeleccionado(producto);
    setSelectionModel([producto.idProducto]);
    setCantidad(1);
    setInsumos([]);

    if (!user || !producto.idReceta) {
      return;
    }

    recetaAbortRef.current?.abort();
    const controller = new AbortController();
    recetaAbortRef.current = controller;

    setLoadingReceta(true);
    fetchGetRecetaProduccion(user, producto.idProducto, producto.idReceta, controller.signal)
      .then((items) => {
        setInsumos(items.map((item) => ({ ...item, cantidadTotal: Number((item.cantidadUnitario * cantidad).toFixed(4)) })));
        if (items.length === 0) {
          showNotification("El producto no tiene insumos cargados en la receta.", "warning");
        }
      })
      .catch((error) => {
        if (!isAbortError(error)) showNotification("No se pudo cargar la receta del producto.", "error");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingReceta(false);
      });
  };

  const actualizarInsumo = useCallback((idProducto: number, field: "cantidadUnitario" | "costo", value: number) => {
    setInsumos((actuales) => actuales.map((item) => {
      if (item.idProducto !== idProducto) {
        return item;
      }

      const actualizado = { ...item, [field]: value };
      return {
        ...actualizado,
        cantidadTotal: Number((actualizado.cantidadUnitario * cantidad).toFixed(4)),
      };
    }));
  }, [cantidad]);

  const eliminarInsumo = (idProducto: number) => {
    setInsumos((actuales) => actuales.filter((item) => item.idProducto !== idProducto));
  };

  const totalCosto = useMemo(
    () => insumos.reduce((total, item) => total + (Number(item.costo || 0) * Number(item.cantidadTotal || 0)), 0),
    [insumos]
  );

  const costoPorUnidad = useMemo(() => cantidad > 0 ? totalCosto / cantidad : 0, [cantidad, totalCosto]);
  const margenSobreCosto = useMemo(() => {
    if (!productoSeleccionado || costoPorUnidad <= 0) {
      return 0;
    }
    return ((productoSeleccionado.precioVenta * 100 / costoPorUnidad) - 100);
  }, [costoPorUnidad, productoSeleccionado]);

  const confirmarProduccion = () => {
    if (!user || !idLocal || !productoSeleccionado) {
      showNotification("Seleccione local y producto.", "warning");
      return;
    }
    if (cantidad <= 0 || insumos.length === 0 || insumos.some((item) => item.cantidadTotal <= 0)) {
      showNotification("Complete cantidad e insumos antes de confirmar.", "warning");
      return;
    }

    setSaving(true);
    fetchConfirmarProduccion(user, {
      idLocal: Number(idLocal),
      idProducto: productoSeleccionado.idProducto,
      producto: productoSeleccionado.producto,
      cantidad,
      costoPorUnidad,
      actualizarCostoProducto: actualizarCosto,
      insumos: insumos.map((item) => ({
        ...item,
        observacion: item.observacion || `Produccion de ${productoSeleccionado.producto}`,
      })),
    })
      .then((result) => {
        showNotification(result.message ?? result.Message ?? "Produccion confirmada correctamente.", "success");
        setProductoSeleccionado(null);
        setSelectionModel([]);
        setInsumos([]);
        setCantidad(1);
        setPaginationModel((current) => ({ ...current }));
      })
      .catch((error) => showNotification(error?.message ?? "No se pudo confirmar la produccion.", "error"))
      .finally(() => setSaving(false));
  };

  const productColumns = useMemo<GridColDef[]>(() => [
    { field: "codigo", headerName: "Codigo", minWidth: 130, flex: 0.6 },
    { field: "producto", headerName: "Producto", minWidth: 250, flex: 1.4 },
    { field: "stock", headerName: "Stock", minWidth: 110, type: "number", flex: 0.5 },
    { field: "costo", headerName: "Costo", minWidth: 120, flex: 0.6, valueFormatter: ({ value }) => toMoney(Number(value)) },
    { field: "precioVenta", headerName: "Venta", minWidth: 120, flex: 0.6, valueFormatter: ({ value }) => toMoney(Number(value)) },
  ], []);

  const insumoColumns = useMemo<GridColDef[]>(() => [
    { field: "codigo", headerName: "Codigo", minWidth: 120, flex: 0.5 },
    { field: "producto", headerName: "Insumo", minWidth: 240, flex: 1.2 },
    {
      field: "cantidadUnitario",
      headerName: "Cant. x unidad",
      minWidth: 150,
      flex: 0.7,
      renderCell: (params) => (
        <TextField
          type="number"
          size="small"
          value={params.row.cantidadUnitario}
          inputProps={{ min: 0, step: "0.001" }}
          onChange={(event) => actualizarInsumo(params.row.idProducto, "cantidadUnitario", Number(event.target.value))}
        />
      ),
    },
    { field: "cantidadTotal", headerName: "Total", minWidth: 120, type: "number", flex: 0.5 },
    {
      field: "costo",
      headerName: "Costo",
      minWidth: 130,
      flex: 0.6,
      renderCell: (params) => (
        <TextField
          type="number"
          size="small"
          value={params.row.costo}
          inputProps={{ min: 0, step: "0.01" }}
          onChange={(event) => actualizarInsumo(params.row.idProducto, "costo", Number(event.target.value))}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "",
      minWidth: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => eliminarInsumo(params.row.idProducto)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [actualizarInsumo]);

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <FactoryIcon color="primary" />
        <Typography variant="h4">Produccion</Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <LocalSelector onLocalChange={setIdLocal} />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField label="Buscar codigo/producto" value={search} onChange={(event) => setSearch(event.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={<Checkbox checked={soloConReceta} onChange={(event) => setSoloConReceta(event.target.checked)} />}
              label="Solo con receta"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => setPaginationModel((current) => ({ ...current }))}
              fullWidth
            >
              Actualizar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {!idLocal ? (
        <Alert severity="info">Seleccione un local para gestionar produccion.</Alert>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Productos</Typography>
              <DataGridViewport height={560} minWidth={820}>
                <DataGrid
                  rows={productos.map((item) => ({ ...item, id: item.idProducto }))}
                  columns={productColumns}
                  rowCount={totalProductos}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[10, 20, 50]}
                  loading={loadingProductos}
                  rowSelectionModel={selectionModel}
                  onRowSelectionModelChange={(selection) => setSelectionModel(selection)}
                  onRowClick={(params) => seleccionarProducto(params.row)}
                  disableMultipleRowSelection
                />
              </DataGridViewport>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {productoSeleccionado?.producto ?? "Produccion"}
              </Typography>
              {productoSeleccionado ? (
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cantidad a producir"
                        type="number"
                        value={cantidad}
                        inputProps={{ min: 0, step: "0.001" }}
                        onChange={(event) => setCantidad(Number(event.target.value))}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={<Checkbox checked={actualizarCosto} onChange={(event) => setActualizarCosto(event.target.checked)} />}
                        label="Actualizar costo"
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Costo total</Typography>
                      <Typography variant="h6">{toMoney(totalCosto)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Costo unitario</Typography>
                      <Typography variant="h6">{toMoney(costoPorUnidad)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Precio venta</Typography>
                      <Typography variant="body1">{toMoney(productoSeleccionado.precioVenta)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">% sobre costo</Typography>
                      <Typography variant="body1">{margenSobreCosto.toFixed(2)}%</Typography>
                    </Grid>
                  </Grid>

                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <FactoryIcon />}
                    onClick={confirmarProduccion}
                    disabled={saving || loadingReceta || insumos.length === 0}
                  >
                    Confirmar produccion
                  </Button>
                </Stack>
              ) : (
                <Alert severity="info">Seleccione un producto para cargar su receta.</Alert>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Insumos</Typography>
              <DataGridViewport height={360} minWidth={860}>
                <DataGrid
                  rows={insumos.map((item) => ({ ...item, id: item.idProducto }))}
                  columns={insumoColumns}
                  loading={loadingReceta}
                  hideFooter
                  disableRowSelectionOnClick
                />
              </DataGridViewport>
            </Paper>
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

export default Produccion;




